import {mark, getInteractionId, types as metrics} from '@servicenow/ui-metrics';
import {getHostInfo, allLogTypes as logTypes} from '@servicenow/ui-internal';
import {customEventDispatcher} from './customEventDispatcher';
import {
	createMetaData,
	appendToPayload,
	isPropertyUpdate,
	normalizeActionMeta,
	validateActionPayload,
	isInternalActionType,
	getInternalActionName,
	isStoreUpdate,
	hasActionHandlerForType,
	skipForDetachedComponent
} from './utils';
import {HELPERS_SCHEDULED, HELPERS_IMMEDIATE, PRESENTATIONAL} from '../symbols';
import {emitNowUiEvent, getHostVariables} from '../utils';
import log from '../log';
import {
	ACTION_DISPATCHED,
	SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED,
	PROPERTIES_SET,
	DISPATCH_EVENT
} from '../constants';
import {addActionHandlerForUnknownType} from './addActionHandlerForUnknownType';

export function createDispatcher(host, dispatcher = customEventDispatcher) {
	const hostVariables = getHostVariables(host);

	return function dispatch(type, payload = {}, meta = {}, error = null) {
		if (type === undefined) {
			log(
				`You are dispatching an action that is type: ${type}.  We will send undefined actions to the upside down place in the next release.`,
				{level: logTypes.WARN, host, origin: 'dispatch'}
			);
		}

		if (skipForDetachedComponent(host, type, payload)) return;

		if (isStoreUpdate(type)) {
			hostVariables[HELPERS_SCHEDULED].updateState(
				payload,
				{
					[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true
				},
				getInteractionId(meta)
			);

			return log(
				`${type} is a private action type and has been deprecated. Please use updateState.`,
				{host, origin: 'dispatch'}
			);
		}

		if (isInternalActionType(type, meta.internal)) {
			const {tagName, componentId} = getHostInfo(host);
			log(`${getInternalActionName(type)} cannot be dispatched as it is an internal action type`, {
				tagName,
				componentId,
				host,
				origin: 'dispatch'
			});

			// Only return if the type is not one of the following, in order to maintain backwards compatibility
			if (![PROPERTIES_SET, DISPATCH_EVENT].includes(type)) return;
		}

		if (isPropertyUpdate(type))
			return hostVariables[HELPERS_IMMEDIATE].updateProperties(payload, getInteractionId(meta));

		validateActionPayload(host, type, payload, hostVariables); // TODO: fix

		// eslint-disable-next-line no-param-reassign
		meta = normalizeActionMeta(meta, {host});

		const action = {
			type,
			payload: meta.retargeted ? payload : appendToPayload(host, payload),
			meta: createMetaData(host, meta, hostVariables),
			error
		};

		emitNowUiEvent(ACTION_DISPATCHED, {host, action});

		if (
			!hostVariables[PRESENTATIONAL] &&
			!meta.internal &&
			!hasActionHandlerForType(hostVariables, type)
		)
			addActionHandlerForUnknownType(host, type, hostVariables);

		mark(host, getInteractionId(action.meta), metrics.DISPATCH, {type});
		return dispatcher(host, action);
	};
}
